import type { ReactNode } from 'react';

/**
 * B-05/B-06: the one way a policy's number reaches the screen.
 *
 * A policy carries two numbers and they are not interchangeable:
 *
 *   insurer_policy_number  D210524290                     printed on the document
 *   policy_number          POL-716-godigit-260807-F798C6  ours, auto-generated
 *
 * Every screen leads with the insurer's. Ours is shown only when the insurer's
 * is blank, and then it is LABELLED as ours -- an unlabelled POL-... slug in a
 * "Policy Number" column reads as the insurer's number, and a broker who quotes
 * it to an insurer gets nowhere.
 *
 * `display_policy_number` (computed server-side by
 * apps.renewals.policy_display.policy_display_number) already applies the same
 * precedence but collapses the two. When a payload carries only that, it is
 * our reference exactly when it equals `policy_number`.
 */
export interface PolicyNumberSource {
  insurer_policy_number?: string | null;
  policy_number?: string | null;
  display_policy_number?: string | null;
}

export interface PolicyNumberParts {
  /** The number to show; '' when the policy carries none at all. */
  text: string;
  /** True when `text` is our internal reference, not the insurer's number. */
  isInternalRef: boolean;
}

export const INTERNAL_REF_LABEL = 'internal ref';

export function policyNumberParts(source: PolicyNumberSource | null | undefined): PolicyNumberParts {
  const theirs = (source?.insurer_policy_number ?? '').trim();
  const ours = (source?.policy_number ?? '').trim();
  // An "insurer" number identical to ours is ours. Two ways that happens: the
  // renewals/servicing serializers (PolicyDisplayMixin) serve a field NAMED
  // insurer_policy_number that is already collapsed to ours when the insurer's
  // is blank; and a handful of rows hold our number in the insurer field
  // (docs/2026-09-20-insurer-policy-number-audit.md). The data is left alone;
  // the screen just says whose number it is.
  if (theirs) return { text: theirs, isInternalRef: theirs === ours };

  const display = (source?.display_policy_number ?? '').trim();
  if (display) {
    // A server-collapsed number with no `policy_number` beside it cannot be
    // told apart; it is the insurer's unless it visibly is ours.
    return { text: display, isInternalRef: display === ours || /^POL-/i.test(display) };
  }
  if (ours) return { text: ours, isInternalRef: true };
  return { text: '', isInternalRef: false };
}

/**
 * For payloads that serve `policy_number` ALREADY collapsed (insurer's, else
 * ours -- the commission calculations, ledger and payout lines) with the
 * insurer's own number beside it. The collapsed value is ours exactly when the
 * insurer's is served and blank. When the insurer's is not served at all there
 * is nothing to tell them apart by, so the number is taken at face value.
 */
export function collapsedPolicyNumberSource(
  policyNumber: string | null | undefined,
  insurerPolicyNumber: string | null | undefined,
): PolicyNumberSource {
  if (insurerPolicyNumber == null) return { insurer_policy_number: policyNumber };
  // Served non-blank: the collapsed value IS the insurer's -- do not hand it
  // over as `policy_number` too, or the two would match and read as ours.
  if (insurerPolicyNumber.trim()) return { insurer_policy_number: insurerPolicyNumber };
  return { policy_number: policyNumber };
}

/**
 * For places that take a plain string -- a `title`, a breadcrumb, a select
 * option, a toast. Our reference carries its label in the string itself.
 */
export function policyNumberLabel(
  source: PolicyNumberSource | null | undefined,
  empty = '—',
): string {
  const { text, isInternalRef } = policyNumberParts(source);
  if (!text) return empty;
  return isInternalRef ? `${text} (${INTERNAL_REF_LABEL})` : text;
}

/** The muted tag drawn after our reference. */
export function InternalRefTag() {
  return (
    <span
      className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 align-middle text-[10px] font-medium uppercase tracking-wide bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
      title="No insurer policy number recorded — this is our internal reference, not the insurer's number"
    >
      {INTERNAL_REF_LABEL}
    </span>
  );
}

/**
 * The number, with the tag when it is ours. `render` wraps the number itself
 * (typically in an EntityLink) so the tag stays outside the link text.
 */
export function PolicyNumber({
  source,
  render,
  empty = '—',
  className,
}: {
  source: PolicyNumberSource | null | undefined;
  render?: (text: string) => ReactNode;
  empty?: ReactNode;
  className?: string;
}) {
  const { text, isInternalRef } = policyNumberParts(source);
  if (!text) return <span className={className}>{empty}</span>;
  return (
    <span className={className}>
      {render ? render(text) : text}
      {isInternalRef && <InternalRefTag />}
    </span>
  );
}
